import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Poll } from '../types/firebase';
import { useAuth } from './AuthContext';
import * as pollService from '../services/pollService';
import { collection, query, orderBy, onSnapshot, Timestamp } from '@firebase/firestore';
import { db } from '../config/firebase';

interface PollsContextType {
  polls: Poll[];
  loading: boolean;
  error: string | null;
  selectedCause: string;
  setSelectedCause: (cause: string) => void;
  votePoll: (pollId: string, optionId: string) => Promise<void>;
  createPoll: (pollData: Omit<Poll, 'id' | 'createdAt' | 'updatedAt' | 'totalVotes' | 'status'>) => Promise<void>;
  deletePoll: (pollId: string) => Promise<void>;
  hasUserVotedInPoll: (poll: Poll) => boolean;
  getUserVotedOption: (poll: Poll) => string | null;
}

const PollsContext = createContext<PollsContextType | undefined>(undefined);

export const PollsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCause, setSelectedCause] = useState('all');

  // Process Firestore data to ensure consistent poll structure
  const processPollData = (doc: any): Poll => {
    const data = doc.data();
    
    const processedPoll = {
      id: doc.id,
      title: data.title || '',
      description: data.description || '',
      cause: data.cause || '',
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt : null,
      endDate: data.endDate instanceof Timestamp ? data.endDate : null,
      totalVotes: typeof data.totalVotes === 'number' ? data.totalVotes : 0,
      status: data.status || 'active',
      settings: {
        allowComments: data.settings?.allowComments ?? true
      },
      createdBy: {
        id: data.createdBy?.id || '',
        name: data.createdBy?.name || 'Anonymous',
        avatar: data.createdBy?.avatar || ''
      },
      options: Array.isArray(data.options) ? data.options.map((option: any) => ({
        id: option.id || '',
        text: option.text || '',
        votes: typeof option.votes === 'number' ? option.votes : 0,
        votedBy: Array.isArray(option.votedBy) ? option.votedBy : []
      })) : [],
    };
    
    return processedPoll;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      // Query for all polls and filter in memory
      // This is more efficient than trying to do complex OR queries in Firestore
      const pollsQuery = query(
        collection(db, 'polls'),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        pollsQuery,
        {
          next: (snapshot) => {
            const allPolls = snapshot.docs.map(doc => processPollData(doc));
            // Only filter by cause if selected
            const filteredPolls = allPolls.filter(poll => {
              if (selectedCause && selectedCause !== 'all' && poll.cause !== selectedCause) {
                return false;
              }
              return true;
            });
            setPolls(filteredPolls);
            setLoading(false);
          },
          error: (err) => {
            console.error('Error in polls subscription:', err);
            setError('Failed to load polls');
            setLoading(false);
          }
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error('Error setting up polls subscription:', err);
      setError('Failed to load polls');
      setLoading(false);
    }
  }, [selectedCause]);

  const votePoll = async (pollId: string, optionId: string) => {
    if (!currentUser) {
      setError('You must be logged in to vote');
      return;
    }

    try {
      setError(null);
      await pollService.votePoll(pollId, optionId, currentUser.uid);
    } catch (err: any) {
      console.error('Error voting:', err);
      setError(err.message || 'Failed to vote');
    }
  };

  const createPoll = async (pollData: Omit<Poll, 'id' | 'createdAt' | 'updatedAt' | 'totalVotes' | 'status'>) => {
    if (!currentUser) {
      setError('You must be logged in to create a poll');
      return;
    }

    try {
      setError(null);
      await pollService.createPoll(pollData, currentUser);
    } catch (err: any) {
      console.error('Error creating poll:', err);
      setError(err.message || 'Failed to create poll');
    }
  };

  const deletePoll = async (pollId: string) => {
    if (!currentUser) {
      setError('You must be logged in to delete a poll');
      return;
    }

    try {
      setError(null);
      await pollService.deletePoll(pollId, currentUser.uid);
    } catch (err: any) {
      console.error('Error deleting poll:', err);
      setError(err.message || 'Failed to delete poll');
    }
  };

  const hasUserVotedInPoll = useCallback((poll: Poll): boolean => {
    if (!currentUser || !poll.options || !Array.isArray(poll.options)) return false;
    return poll.options.some(option => 
      Array.isArray(option.votedBy) && option.votedBy.includes(currentUser.uid)
    );
  }, [currentUser]);

  const getUserVotedOption = useCallback((poll: Poll): string | null => {
    if (!currentUser || !poll.options || !Array.isArray(poll.options)) return null;
    const votedOption = poll.options.find(option => 
      Array.isArray(option.votedBy) && option.votedBy.includes(currentUser.uid)
    );
    return votedOption ? votedOption.id : null;
  }, [currentUser]);

  const value = {
    polls,
    loading,
    error,
    selectedCause,
    setSelectedCause,
    votePoll,
    createPoll,
    deletePoll,
    hasUserVotedInPoll,
    getUserVotedOption,
  };

  return (
    <PollsContext.Provider value={value}>
      {children}
    </PollsContext.Provider>
  );
};

export const usePolls = () => {
  const context = useContext(PollsContext);
  if (context === undefined) {
    throw new Error('usePolls must be used within a PollsProvider');
  }
  return context;
}; 